/**
 * Os ícones do sistema — <b>desenhados aqui</b>, em SVG, um a um.
 *
 * <h2>Por que não uma biblioteca</h2>
 *
 * Duas razões, e nenhuma delas é gosto:
 *
 * <ul>
 *   <li><b>A política de segurança da página bloqueia host externo.</b> Um
 *       pacote de ícones servido por CDN não carrega — e o que não carrega
 *       vira quadrado vazio de 44px prometendo tela;</li>
 *   <li><b>o sistema precisa abrir sem internet.</b> Ele é consultado do chão
 *       de fábrica, e a rede de lá cai. Ícone é o que resta de um menu
 *       recolhido: sem ele não há menu.</li>
 * </ul>
 *
 * <h2>O desenho é o rótulo</h2>
 *
 * Na faixa recolhida o ícone é a ÚNICA coisa visível do item. Ele é lido de
 * relance, de luva, num monitor de galpão — então cada um tem uma
 * <b>silhueta própria</b>, e é a silhueta que se reconhece a três metros:
 *
 * <ul>
 *   <li><b>painel</b> — um mostrador: arco com ponteiro. Nada mais no sistema
 *       é redondo, e "painel de risco" é literalmente um conjunto de
 *       indicadores;</li>
 *   <li><b>fornecedores</b> — um caminhão: caixa, cabine e duas rodas. É quem
 *       ENTREGA o material. O prédio, que seria o outro candidato, fica
 *       reservado para "Empresas" — e dois ícones de prédio no mesmo menu
 *       seriam o defeito que este arquivo existe para evitar;</li>
 *   <li><b>materias-primas</b> — um tambor: elipse no topo, corpo alto, dois
 *       aros. É o objeto físico em que a etiqueta do sistema vai colada.</li>
 * </ul>
 *
 * Redondo, retangular deitado, cilíndrico em pé: as três silhuetas não se
 * confundem nem borradas. <b>Se dois ficarem parecidos, o problema é o
 * desenho</b> — a correção é aqui, nunca no usuário.
 *
 * <h2>Traço, e não preenchimento</h2>
 *
 * Contorno de 1.75px em `currentColor`: o ícone herda a cor do item de
 * navegação e acompanha de graça o realce do item ativo e o esmaecido.
 * Preenchimento sólido no tamanho de 24px empasta o desenho e apaga
 * justamente a diferença de silhueta.
 *
 * <p>`aria-hidden` em todos, sem exceção: quem nomeia o controle é o
 * `aria-label` do link ou do botão que o contém. Ícone que se anuncia sozinho
 * faz o leitor de tela ler o desenho ANTES do destino.
 */

import type { ReactNode } from "react";

import "./Icone.css";

/** Ícone de DESTINO — o que um item de navegação pode ser. */
export type IconeDeDestino = "painel" | "fornecedores" | "materias-primas";

/** Ícone de CONTROLE — o que um botão da própria interface faz. */
export type IconeDeControle = "recolher" | "esticar";

export type NomeDeIcone = IconeDeDestino | IconeDeControle;

const DESENHOS: Record<NomeDeIcone, ReactNode> = {
  // Mostrador: arco de 180°, ponteiro apontando para o alto à direita e o
  // eixo cheio. O eixo é o único preenchimento do conjunto — sem ele o
  // ponteiro fica solto no meio do arco.
  painel: (
    <>
      <path d="M3.5 17.5a8.5 8.5 0 0 1 17 0" />
      <path d="M12 17.5l4.2-4.6" />
      <circle cx="12" cy="17.5" r="1.35" fill="currentColor" stroke="none" />
    </>
  ),

  // Caminhão: baú, cabine com o capô recuado e duas rodas. As rodas passam
  // por baixo da linha do baú de propósito — é o que dá a silhueta de
  // veículo, e não de dois retângulos.
  fornecedores: (
    <>
      <path d="M2.5 6.5h10.5v10H2.5z" />
      <path d="M13 9.5h4.2l3.3 3.3v3.7H13z" />
      <circle cx="6.75" cy="18.25" r="1.75" />
      <circle cx="16.75" cy="18.25" r="1.75" />
    </>
  ),

  // Tambor: a elipse do topo é o que diz "cilindro" — sem ela o desenho é um
  // retângulo com duas listras, que é qualquer coisa.
  "materias-primas": (
    <>
      <ellipse cx="12" cy="6" rx="6" ry="2.25" />
      <path d="M6 6v12c0 1.24 2.69 2.25 6 2.25s6-1.01 6-2.25V6" />
      <path d="M6 10.75h12" />
      <path d="M6 14.5h12" />
    </>
  ),

  // Duas setas apontando para onde a coluna vai — o gesto mais reconhecido
  // que existe para encolher e esticar um painel. O nome do botão diz o
  // resto, e é ele que o leitor de tela anuncia.
  recolher: (
    <>
      <path d="M11.5 6.5l-5 5.5 5 5.5" />
      <path d="M18 6.5l-5 5.5 5 5.5" />
    </>
  ),
  esticar: (
    <>
      <path d="M12.5 6.5l5 5.5-5 5.5" />
      <path d="M6 6.5l5 5.5-5 5.5" />
    </>
  ),
};

interface Props {
  nome: NomeDeIcone;
}

export function Icone({ nome }: Props) {
  return (
    <svg
      className="ui-icone"
      viewBox="0 0 24 24"
      width="24"
      height="24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {DESENHOS[nome]}
    </svg>
  );
}
